<template>
    <div>
        <Portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex justify-start">
                <VcInput
                    v-model="searchQuery"
                    prefix-icon="Search"
                    :placeholder="String(t('general.search'))"
                    size="mini"
                    class="mx-2"
                />
            </div>
        </Portal>
        <redesign-data-table-component
            v-if="searchedTableData.length && columns.length"
            :widget-data="data"
            :columns-config="columnsConfig"
            :columns="columns"
            :table-data="searchedTableData"
            :row-key="getMultiQueueElTableRowKeyAsColumns"
            :stripe="true"
            :style="tableStatusesColorsConfig"
            :default-available-columns="defaultVisibleColumnsForAsColumns"
            fix-first-column
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <div :style="{color: column.textColor}">
                    <div
                        v-if="getCellStat(row, column.prop) === multiQueueColumnsPropNames.CALLERS"
                    >
                        <redesign-callers-count
                            :class="getCallersStatusClass(column.prop)"
                            :queue-id="getQueueId(column.prop)"
                            :queue-name="getQueueName(column.prop)"
                            :warning-value="maximumWarningCallersValueFromSettings"
                            :filtered-queues="filteredQueues"
                            :limit-value="maximumLimitCallersValueFromSettings"
                            @update-callers-value="updateMaxCallerCount"
                            @clear-queue-max-callers="removeQueueFromMaxCallers"
                        />
                    </div>
                    <div
                        v-else-if="getCellStat(row, column.prop) === multiQueueColumnsPropNames.CURRENT_WAIT_TIME"
                    >
                        <redesign-max-wait-time
                            :class="getWaitTimeStatusClass(column.prop)"
                            :queue-id="getQueueId(column.prop)"
                            :queue-name="getQueueName(column.prop)"
                            :limit-value="maximumLimitWaitTimeValueFromSettings"
                            :warning-value="maximumWarningWaitTimeValueFromSettings"
                            :filtered-queues="filteredQueues"
                            @update-wait-time-value="updateCurrentMaxWaitTime"
                            @clear-calls="removeQueueFromMaxWaitTime"
                        />
                    </div>
                    <div
                        v-else-if="getCellStat(row, column.prop) === multiQueueColumnsPropNames.ACTIVE_QUEUE_CALLS"
                    >
                        <redesign-active-queue-calls
                            :queue-id="getQueueId(column.prop)"
                            :table-data="tableDataForActiveCalls"
                        />
                    </div>
                    <RedesignAgentsCountCell
                        v-else-if="getCellStat(row, column.prop) === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_AVAILABLE"
                        :queue-id="getQueueId(column.prop)"
                        :available="true"
                        :agents="agents"
                    />
                    <RedesignAgentsCountCell
                        v-else-if="getCellStat(row, column.prop) === multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_UNAVAILABLE"
                        :queue-id="getQueueId(column.prop)"
                        :available="false"
                        :agents="agents"
                    />
                    <span v-else-if="hasCellValue(row, column.prop)">
                        <span
                            :style="{color: getColumnTextColor(getRowStat(row))}"
                        >
                            {{ getCellValue(row, column.prop) }}
                        </span>
                    </span>
                </div>
            </template>
        </redesign-data-table-component>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, toRefs, watch } from 'vue'
import { ALL_QUEUE_ID } from '@/enum/redesignQueueDashboardStatistics'
import { useI18n } from 'vue-i18n'
import {
    defaultRowData,
    multiQueueColumnsPropNames,
    QueueData
} from '@/helpers/multiQueueDashboardRedesign'
import { getMultiQueueElTableRowKeyAsColumns, useMultiQueueTableCommon } from '@/composables/useMultiQueueTableCommon'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import RedesignCallersCount from '@/components/RedesignComponents/Queue/RedesignCallersCount.vue'
import RedesignMaxWaitTime from '@/components/RedesignComponents/Queue/RedesignMaxWaitTime.vue'
import { ColumnsObjectConfig, TableHeadersConfig, WidgetExtended } from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { VcInput } from '@voicenter-team/voicenter-ui-plus'
import RedesignActiveQueueCalls from '@/components/RedesignComponents/Queue/RedesignActiveQueueCalls.vue'
import RedesignAgentsCountCell from '@/components/RedesignComponents/Queue/RedesignAgentsCountCell.vue'
import { useQueueAgentsInline } from '@/composables/useQueueAgentsInline'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'

const { t } = useI18n()

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void
}>()

const props = defineProps<{
    data: WidgetExtended
    queueTableData: QueueData[]
}>()

const { data: widgetData } = toRefs(props)
const searchQuery = ref('')
const columnsConfig = ref<ColumnsObjectConfig>({})

const displayRowWithTotal = computed(() => {
    return props.data.WidgetLayout?.displayRowWithTotals ?? true
})
const showStatsInPercentage = computed(() => {
    return props.data.WidgetLayout?.showStatsInPercentage ?? false
})

const {
    mappedBaseTableData,
    filteredQueues,
    maximumWarningCallersValueFromSettings,
    maximumLimitCallersValueFromSettings,
    maximumWarningWaitTimeValueFromSettings,
    maximumLimitWaitTimeValueFromSettings,
    updateMaxCallerCount,
    removeQueueFromMaxCallers,
    updateCurrentMaxWaitTime,
    removeQueueFromMaxWaitTime,
    getCallersStatusClass,
    getWaitTimeStatusClass,
    getColumnTextColor
} = useMultiQueueTableCommon(
    widgetData,
    toRef(props, 'queueTableData'),
    showStatsInPercentage
)

const { getAgents } = useQueueAgentsInline()

const { makeTableHeaderTranslations, makeTableHeaderTooltipTranslations } = useTableHeaderTranslations()

const agents = computed(() => {
    return getAgents() || {
        allAvailable: [],
        allUnavailable: []
    }
})

// Get all available stat types from defaultRowData
const availableStatTypes = computed(() =>
    Object.keys(defaultRowData).filter(
        key => key !== multiQueueColumnsPropNames.QUEUE_NAME && key !== 'queue_id'
    )
)

const columns = computed(() => {
    const queueColumns = filteredQueues.value.map(queue => ({
        prop: String(queue.queue_id),
        label: {
            shortTextLabel: queue.q_name,
            fullTextLabel: queue.q_name,
        },
        tooltipLabel: queue.q_name,
        textColor: getColumnTextColor(queue.queue_id),
    }))

    const cols = [
        {
            prop: 'Statistic',
            label: {
                shortTextLabel: 'Stat Type',
                fullTextLabel: 'Stat Type'
            },
            tooltipLabel: 'Stat Type',
            filterableConfig: {
                componentProps: {
                    filterOptions: availableStatTypes.value
                }
            }
        },
        ...queueColumns,
    ]

    if (displayRowWithTotal.value) {
        cols.push({
            prop: ALL_QUEUE_ID,
            label: {
                shortTextLabel: ALL_QUEUE_ID,
                fullTextLabel: ALL_QUEUE_ID
            },
            tooltipLabel: ALL_QUEUE_ID
        })
    }

    return cols.map(col => {
        const baseLabel = typeof col.label === 'string'
            ? col.label
            : col.label?.fullTextLabel || col.prop

        return {
            ...col,
            label: makeTableHeaderTranslations(col, baseLabel),
            tooltipLabel: makeTableHeaderTooltipTranslations(col, baseLabel)
        }
    })
})

const defaultVisibleColumnsForAsColumns = computed(() => {
    const visibleColumns: Record<string, boolean> = {
        Statistic: true
    }

    filteredQueues.value.forEach(queue => {
        visibleColumns[String(queue.queue_id)] = true
    })

    if (displayRowWithTotal.value) {
        visibleColumns[ALL_QUEUE_ID] = true
    }

    return visibleColumns
})

type ColumnTableRow = {
    Statistic: string
    __stat: string
    [queueColumn: string]: string | number
}

const queueNameById = computed(() => {
    const nameMap = new Map<string, string>()

    filteredQueues.value.forEach(queue => {
        nameMap.set(String(queue.queue_id), queue.q_name)
    })

    if (displayRowWithTotal.value) {
        nameMap.set(ALL_QUEUE_ID, ALL_QUEUE_ID)
    }

    return nameMap
})

const mappedTableData = computed<ColumnTableRow[]>(() => {
    const statistics = availableStatTypes.value

    if (!mappedBaseTableData.value || !Object.keys(mappedBaseTableData.value).length) {
        return []
    }

    const baseDataArray = Object.values(mappedBaseTableData.value)

    return statistics.map(stat => {
        const row: ColumnTableRow = {
            Statistic: stat,
            __stat: stat
        }

        baseDataArray.forEach((queue) => {
            const key = String(queue.queue_id)
            row[key] = queue[stat]
        })

        if (displayRowWithTotal.value) {
            const totalValue = baseDataArray.reduce((sum, queue) => {
                const value = queue[stat]
                if (typeof value === 'number') {
                    return sum + value
                }
                return sum
            }, 0)

            // Fix floating point precision for AvgAnswerHourly and AnsweredWithCBPercent
            let formattedValue: number | string = totalValue
            if (typeof totalValue === 'number') {
                if (stat === multiQueueColumnsPropNames.AVG_ANSWER_HOURLY ||
                    stat === multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT) {
                    formattedValue = parseFloat(totalValue.toFixed(2))
                }
            }

            row[ALL_QUEUE_ID] = formattedValue
        }

        return row
    })
})

const tableDataForActiveCalls = computed(() => {
    return Object.values(mappedBaseTableData.value)
})

const searchedTableData = computed(() => {
    if (!mappedTableData.value.length) {
        return mappedTableData.value
    }
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return mappedTableData.value

    return mappedTableData.value.filter(item =>
        Object.entries(item).some(([ key, val ]) => {
            if (key === '__stat') {
                return false
            }
            return String(val).toLowerCase().includes(query)
        })
    )
})

const maximumWarningCallersColor = computed(() => props.data?.WidgetLayout?.maximumWarningCallersColor || '')
const maximumLimitCallersColor = computed(() => props.data?.WidgetLayout?.maximumLimitCallersColor || '')
const maximumWarningMaxRingTimeColor = computed(() => props.data?.WidgetLayout?.maximumWarningMaxRingTimeColor || '')
const maximumLimitMaxRingTimeColor = computed(() => props.data?.WidgetLayout?.maximumLimitMaxRingTimeColor || '')
const tableStatusesColorsConfig = computed(() => {
    return {
        '--maximum-warning-callers-color': maximumWarningCallersColor.value,
        '--maximum-limit-callers-color': maximumLimitCallersColor.value,
        '--maximum-warning-max-ring-time-color': maximumWarningMaxRingTimeColor.value,
        '--maximum-limit-max-ring-time-color': maximumLimitMaxRingTimeColor.value
    }
})

const getQueueId = (columnProp: string | number): number | string => {
    return columnProp === ALL_QUEUE_ID ? ALL_QUEUE_ID : Number(columnProp)
}

const getQueueName = (columnProp: string | number): string => {
    return queueNameById.value.get(String(columnProp)) ?? String(columnProp)
}

const getRowStat = (row: ColumnTableRow): string => {
    return row.__stat
}

const getCellStat = (row: ColumnTableRow, columnProp: string | number): string | null => {
    if (String(columnProp) === 'Statistic') {
        return null
    }
    return row.__stat
}

const getCellValue = (row: ColumnTableRow, columnProp: string | number): string | number => {
    return row[String(columnProp)] ?? ''
}

const hasCellValue = (row: ColumnTableRow, columnProp: string | number): boolean => {
    return row[String(columnProp)] !== undefined && row[String(columnProp)] !== null
}

const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config) {
        return
    }
    columnsConfig.value = config

    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)

    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsObjectConfig: config
        }
    }
    emit('update-widget', updatedWidgetData)
}

const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    if (config) {
        columnsConfig.value = config
    }

    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)

    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config ? { columnsObjectConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}

const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}

watch(() => props.data.WidgetLayout, (val) => {
    if (val) {
        columnsConfig.value = val?.columnsObjectConfig ?? {}
    }
}, {
    immediate: true,
})
</script>

<style scoped lang="scss">
</style>


